/**
 * The fixture behind `/__ui/automations` and the unit tests.
 *
 * Its board is the sandbox board the captures were taken on (18430299563, "Sandbox – Column
 * types"), and its four automations are the four the sandbox ended the pass with
 * (`63-after-cleanup.png`): the date-arrives notify, the two loop-pair status recipes and the
 * pre-made update. That is what makes a screenshot of this harness comparable to the capture.
 */
import { statusColorHex } from '@elizmonday/ui';
import {
  automationBlockRegistry,
  renderRecipeSentence,
  templatesInSection,
  AUTOMATION_TEMPLATES,
  TEMPLATE_BASICS,
  TEMPLATE_SECTIONS,
  TEMPLATE_SECTION_BLURB,
  type AutomationRecipe,
} from '@elizmonday/shared';
import type {
  AutomationDetailView,
  AutomationSummaryView,
  AutomationsSource,
  PickerDataView,
  RunRowView,
  RunStepView,
  TemplateGalleryView,
} from './types';

export const FIXTURE_BOARD_ID = '18430299563';
export const FIXTURE_BOARD_NAME = 'Sandbox – Column types';

const ROBERTO = { id: '4066133429', name: 'Roberto', photoUrl: null };

export const FIXTURE_PICKERS: PickerDataView = {
  columns: [
    {
      id: 'status',
      title: 'Status',
      type: 'status',
      isSubitemColumn: false,
      labels: [
        { index: 2, text: 'Stuck', color: statusColorHex('red_shadow') },
        { index: 0, text: 'Working on it', color: statusColorHex('orange') },
        { index: 1, text: 'Done', color: statusColorHex('green_shadow') },
      ],
    },
    { id: 'dropdown', title: 'Dropdown', type: 'dropdown', isSubitemColumn: false, labels: [
      { index: 1, text: 'One', color: null },
      { index: 2, text: 'Two', color: null },
    ] },
    { id: 'text', title: 'Text (renamed)', type: 'text', isSubitemColumn: false, labels: [] },
    { id: 'long_text', title: 'Long Text', type: 'long_text', isSubitemColumn: false, labels: [] },
    { id: 'numbers', title: 'Numbers', type: 'numbers', isSubitemColumn: false, labels: [] },
    { id: 'date4', title: 'Date', type: 'date', isSubitemColumn: false, labels: [] },
    { id: 'timeline', title: 'Timeline start date', type: 'timeline', isSubitemColumn: false, labels: [] },
    { id: 'people', title: 'People', type: 'people', isSubitemColumn: false, labels: [] },
  ],
  subitemColumns: [{ id: 'date_sub', title: 'Date', type: 'date', isSubitemColumn: true, labels: [] }],
  groups: [
    { id: 'done_group', title: 'Done', color: statusColorHex('orange') },
    { id: 'group_2', title: 'Group 2', color: statusColorHex('red_shadow') },
    { id: 'group_1', title: 'Group 1', color: statusColorHex('green_shadow') },
  ],
  people: [
    { id: ROBERTO.id, name: 'Roberto', email: 'roberto@elizetaplus.com', photoUrl: null },
    { id: '5000000001', name: 'Dana', email: 'dana@elizetaplus.com', photoUrl: null },
  ],
  boards: [
    { id: '18430299564', name: 'Sandbox – Views' },
    { id: '18430299565', name: 'Sandbox – Automations' },
  ],
  me: { id: ROBERTO.id, name: 'Roberto' },
  /**
   * M6-A4: the state a fresh install is in — Slack connected, the rest not — so the harness
   * shows both halves of the integration slots' "not connected" behaviour.
   */
  connections: { slack: 'connected', gmail: 'not_connected', googleCalendar: 'not_connected', webhooks: 'connected' },
};

const recipes: Record<string, AutomationRecipe> = {
  '1': {
    version: 1,
    trigger: {
      nodeId: 'n0',
      block: '580',
      params: { columnId: 'date4', dateTriggerConfig: { hour: 7.25, utcDaysDiff: 0, offset: { count: 1, unit: 'days' }, timezone: 'UTC' } },
      inputs: {},
    },
    conditions: [],
    actions: [
      { nodeId: 'n1', block: '5100', params: { message: 'Heads up', receiver: { type: 'user', value: ROBERTO.id } }, inputs: {}, delay: null },
    ],
  },
  '2': {
    version: 1,
    trigger: { nodeId: 'n0', block: '223025', params: { columnId: 'status', statusColumnValue: { index: 2 } }, inputs: {} },
    conditions: [{ nodeId: 'n1', block: '1838', params: { columnId: 'status', isCondition: 'is', columnValue: { index: 2 } }, inputs: {} }],
    actions: [{ nodeId: 'n2', block: '41', params: { columnId: 'status', statusColumnValue: { index: 0 } }, inputs: {}, delay: null }],
  },
  '3': {
    version: 1,
    trigger: { nodeId: 'n0', block: '223025', params: { columnId: 'status', statusColumnValue: { index: 0 } }, inputs: {} },
    conditions: [{ nodeId: 'n1', block: '1838', params: { columnId: 'status', isCondition: 'is', columnValue: { index: 0 } }, inputs: {} }],
    actions: [{ nodeId: 'n2', block: '41', params: { columnId: 'status', statusColumnValue: { index: 2 } }, inputs: {}, delay: null }],
  },
  '4': {
    version: 1,
    trigger: { nodeId: 'n0', block: '223025', params: { columnId: 'status', statusColumnValue: { index: 1 } }, inputs: {} },
    conditions: [],
    actions: [{ nodeId: 'n1', block: 'ez.a.create_premade_update', params: {}, inputs: {}, delay: null }],
  },
  /** R7: a recipe the registry could not build — kept, disabled, annotated. */
  '5': {
    version: 1,
    trigger: { nodeId: 'n0', block: '55', params: {}, inputs: {} },
    conditions: [],
    actions: [],
  },
  /**
   * P-032: the shape 9 of board `717364248`'s 20 imported automations are in — an **empty
   * `title`** beside an intact `recipe`, because their creator was deactivated. The centre has
   * to render the sentence anyway, from the recipe.
   */
  '6': {
    version: 1,
    trigger: { nodeId: 'n0', block: '223025', params: { columnId: 'status', statusColumnValue: { index: 1 } }, inputs: {} },
    conditions: [],
    actions: [{ nodeId: 'n1', block: '41', params: { columnId: 'status', statusColumnValue: { index: 2 } }, inputs: {}, delay: null }],
  },
};

const minutesAgo = (minutes: number): string => new Date(Date.parse('2026-09-10T18:15:00.000Z') - minutes * 60_000).toISOString();

const summary = (
  id: string,
  title: string,
  overrides: Partial<AutomationSummaryView> = {},
): AutomationSummaryView => ({
  id,
  boardId: FIXTURE_BOARD_ID,
  title,
  description: null,
  app: 'monday',
  ownerId: ROBERTO.id,
  owner: ROBERTO,
  active: true,
  state: 'active',
  noticeMessage: null,
  mappingStatus: 'mapped',
  stats: { runs: 0, succeeded: 0, failed: 0 },
  runnable: true,
  updatedAt: minutesAgo(6),
  lastRunAt: null,
  ...overrides,
});

export const FIXTURE_AUTOMATIONS: AutomationSummaryView[] = [
  summary('1', '1 day before Date arrives notify Roberto', { updatedAt: minutesAgo(6) }),
  summary('2', 'When Status changes to Stuck and only if Status is Stuck set Status to Working on it', {
    updatedAt: minutesAgo(20),
    active: false,
    state: 'deactivated',
    runnable: false,
    stats: { runs: 12, succeeded: 12, failed: 0 },
    lastRunAt: minutesAgo(21),
  }),
  summary('3', 'When Status changes to Working on it and only if Status is Working on it set Status to Stuck', {
    updatedAt: minutesAgo(20),
    active: false,
    state: 'deactivated',
    runnable: false,
    stats: { runs: 11, succeeded: 11, failed: 0 },
    lastRunAt: minutesAgo(21),
  }),
  summary('4', 'When Status changes to Done, create a pre-made update', { updatedAt: minutesAgo(0) }),
  summary('5', 'When an item is created …', {
    active: false,
    state: 'auto_disabled_by_user',
    runnable: false,
    noticeMessage: 'store.monday.automation.error.creator_deactivated',
    mappingStatus: 'unmapped',
    updatedAt: minutesAgo(180),
  }),
  /* P-032: the imported row with no stored sentence — the centre rebuilds it from the recipe. */
  summary('6', '', {
    active: false,
    state: 'deactivated',
    runnable: false,
    noticeMessage: 'store.monday.automation.error.creator_deactivated',
    updatedAt: minutesAgo(4320),
  }),
];

const RUN_SENTENCE = FIXTURE_AUTOMATIONS[1]!.title;

export const FIXTURE_RUNS: RunRowView[] = [
  {
    id: '900',
    automationId: '2',
    automationTitle: RUN_SENTENCE,
    automationDeleted: false,
    itemId: '13009367605',
    itemName: 'Bulk A',
    status: 'succeeded',
    chainId: 'chain-1',
    depth: 1,
    errorCode: null,
    triggeredBy: 'automation',
    startedAt: minutesAgo(21),
    finishedAt: new Date(Date.parse(minutesAgo(21)) + 3_000).toISOString(),
    durationMs: 3_000,
  },
  {
    id: '901',
    automationId: '3',
    automationTitle: FIXTURE_AUTOMATIONS[2]!.title,
    automationDeleted: false,
    itemId: '13009367605',
    itemName: 'Bulk A',
    status: 'succeeded',
    chainId: 'chain-1',
    depth: 2,
    errorCode: null,
    triggeredBy: 'automation',
    startedAt: minutesAgo(21),
    finishedAt: new Date(Date.parse(minutesAgo(21)) + 2_000).toISOString(),
    durationMs: 2_000,
  },
  {
    id: '902',
    automationId: '77',
    automationTitle: null,
    automationDeleted: true,
    itemId: null,
    itemName: null,
    status: 'failed',
    chainId: 'chain-9',
    depth: 0,
    errorCode: 'action_failed',
    triggeredBy: 'user',
    startedAt: minutesAgo(120),
    finishedAt: minutesAgo(120),
    durationMs: 120,
  },
];

const FIXTURE_STEPS: Record<string, RunStepView[]> = {
  '900': [
    { seq: 0, kind: 'trigger', nodeId: 'n0', block: '223025', status: 'matched', durationMs: null, error: null, output: {} },
    { seq: 1, kind: 'condition', nodeId: 'n1', block: '1838', status: 'passed', durationMs: 12, error: null, output: { passed: true } },
    { seq: 2, kind: 'action', nodeId: 'n2', block: '41', status: 'succeeded', durationMs: 2_900, error: null, output: { changed: true } },
  ],
};

export function fixtureTemplates(): TemplateGalleryView {
  return {
    basics: [...TEMPLATE_BASICS],
    sections: TEMPLATE_SECTIONS.map((section) => ({
      section,
      blurb: TEMPLATE_SECTION_BLURB[section],
      templates: [...templatesInSection(section)],
    })),
    templates: [...AUTOMATION_TEMPLATES],
  };
}

/** The title a newly created automation gets, rendered the way the api renders it. */
export function fixtureTitleFor(recipe: AutomationRecipe): string {
  return renderRecipeSentence(recipe, ({ param, value }) => {
    if (param.toLowerCase().endsWith('columnid') || param === 'columnId') {
      const id = typeof value === 'string' ? value : (value as { columnId?: string }).columnId;
      return FIXTURE_PICKERS.columns.find((column) => column.id === id)?.title ?? null;
    }
    return null;
  });
}

/** An in-memory `AutomationsSource`; writes mutate the arrays so the screens really update. */
export function createFixtureSource(): AutomationsSource {
  const rows = FIXTURE_AUTOMATIONS.map((row) => ({ ...row }));
  const runs = FIXTURE_RUNS.map((row) => ({ ...row }));
  const recipeById: Record<string, AutomationRecipe> = { ...recipes };
  // A copy, not the module constant: a column added through the picker belongs to this source.
  const pickers: PickerDataView = { ...FIXTURE_PICKERS, columns: [...FIXTURE_PICKERS.columns] };
  let nextId = 100;

  const detail = (row: AutomationSummaryView): AutomationDetailView => ({
    ...row,
    recipe: recipeById[row.id] ?? null,
    legacyConfig: null,
  });

  return {
    async list(input) {
      return rows.filter((row) => {
        if (input.search !== undefined && input.search.length > 0) {
          if (!row.title.toLowerCase().includes(input.search.toLowerCase())) return false;
        }
        if (input.state !== undefined && row.state !== input.state) return false;
        if (input.ownerId !== undefined && row.ownerId !== input.ownerId) return false;
        return true;
      });
    },
    async get(id) {
      const row = rows.find((entry) => entry.id === id);
      if (row === undefined) throw new Error(`no automation ${id}`);
      return detail(row);
    },
    async create(input) {
      const id = String(nextId++);
      recipeById[id] = input.recipe;
      rows.unshift(
        summary(id, input.title ?? fixtureTitleFor(input.recipe), { updatedAt: new Date().toISOString() }),
      );
      return { id };
    },
    async update(input) {
      const row = rows.find((entry) => entry.id === input.id);
      if (row !== undefined) {
        if (input.recipe !== undefined) {
          recipeById[input.id] = input.recipe;
          row.title = input.title ?? fixtureTitleFor(input.recipe);
        }
        if (input.description !== undefined) row.description = input.description;
        row.updatedAt = new Date().toISOString();
      }
      return { id: input.id };
    },
    async setActive({ id, active }) {
      const row = rows.find((entry) => entry.id === id);
      if (row === undefined) throw new Error(`no automation ${id}`);
      if (active && row.state !== 'active' && row.mappingStatus !== 'mapped') {
        throw new Error(row.noticeMessage ?? 'ez.automation.unmapped_recipe');
      }
      row.active = active;
      row.state = active ? 'active' : 'deactivated';
      row.runnable = active;
      return { id, active, state: row.state };
    },
    async duplicate({ id }) {
      const source = rows.find((entry) => entry.id === id);
      const newId = String(nextId++);
      if (source !== undefined) {
        const recipe = recipeById[id];
        if (recipe !== undefined) recipeById[newId] = recipe;
        rows.unshift(summary(newId, source.title, { active: false, state: 'deactivated', runnable: false }));
      }
      return { id: newId };
    },
    async remove(id) {
      const at = rows.findIndex((entry) => entry.id === id);
      if (at >= 0) rows.splice(at, 1);
      return { ok: true };
    },
    async runs(input) {
      return runs.filter((row) => (input.automationId === undefined ? true : row.automationId === input.automationId));
    },
    async runSteps(runId) {
      return FIXTURE_STEPS[runId] ?? [];
    },
    async pickers() {
      return pickers;
    },
    async templates() {
      return fixtureTemplates();
    },
    /** P-200: the picker's "+ Add a new column" row, answered in memory like every other write. */
    async createColumn({ type, title }) {
      const id = `${type}_fixture${String(nextId++)}`;
      const column = { id, title: title ?? type, type, isSubitemColumn: false, labels: [] };
      pickers.columns.push(column);
      return { id, type, title: column.title };
    },
  };
}

/** The block registry is a module-level singleton; touching it here keeps tree-shaking honest. */
export const fixtureBlockCount = (): number => automationBlockRegistry.list().length;
